import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Chip } from 'primereact/chip';
import { Card } from 'primereact/card';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { TabView, TabPanel } from 'primereact/tabview';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import LoanService from '../services/LoanService';

const LoanTypes = () => {
    const [loanTypes, setLoanTypes] = useState([]);
    const [selectedLoanTypes, setSelectedLoanTypes] = useState([]);
    const [loanType, setLoanType] = useState({});
    const [loanTypeDialog, setLoanTypeDialog] = useState(false);
    const [deleteLoanTypeDialog, setDeleteLoanTypeDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [nameExists, setNameExists] = useState(false);
    
    // Field Configuration State (NEW)
    const [fieldConfigDialog, setFieldConfigDialog] = useState(false);
    const [selectedLoanTypeForFields, setSelectedLoanTypeForFields] = useState(null);
    const [loanTypeFields, setLoanTypeFields] = useState([]);
    const [newFieldDialog, setNewFieldDialog] = useState(false);
    const [newField, setNewField] = useState({
        field_name: '',
        field_label: '',
        field_type: 'text',
        field_group: 'personal_info',
        is_required: false,
        applicable_to: 'all'
    });
    
    // Edit and Delete Field State
    const [editFieldDialog, setEditFieldDialog] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [deleteFieldDialog, setDeleteFieldDialog] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState(null);
    
    // Force delete state for loan types with associated products
    const [forceDeleteDialog, setForceDeleteDialog] = useState(false);
    const [associatedProductsCount, setAssociatedProductsCount] = useState(0);
    
    // Document Requirements State
    const [documentRequirements, setDocumentRequirements] = useState([]);
    const [addDocumentDialog, setAddDocumentDialog] = useState(false);
    const [newDocument, setNewDocument] = useState({
        document_name: '',
        document_type: 'identity',
        is_mandatory: true,
        description: '',
        alternative_documents: []
    });
    
    const toast = useRef(null);
    const dt = useRef(null);

    const [filters, setFilters] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'category': { value: null, matchMode: FilterMatchMode.EQUALS },
        'status': { value: null, matchMode: FilterMatchMode.EQUALS },
        'requires_collateral': { value: null, matchMode: FilterMatchMode.EQUALS }
    });

    const statusOptions = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Draft', value: 'draft' }
    ];

    useEffect(() => {
        loadLoanTypes();
        loadCategories();
        loadStatistics();
    }, []);

    const loadLoanTypes = async () => {
        try {
            setLoading(true);
            const response = await LoanService.getLoanTypes();
            if (response.success) {
                setLoanTypes(response.data.data || response.data);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load loan types', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await LoanService.getLoanCategories();
            if (response.success) {
                setCategories(response.data);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            // Set default categories if API fails
            setCategories([
                { label: 'Personal Loan', value: 'personal' },
                { label: 'Home Loan', value: 'home_loan' },
                { label: 'Auto Loan', value: 'auto_loan' },
                { label: 'Business Loan', value: 'business_loan' },
                { label: 'Education Loan', value: 'education_loan' },
                { label: 'Gold Loan', value: 'gold_loan' },
                { label: 'Agriculture Loan', value: 'agriculture_loan' },
                { label: 'Construction Loan', value: 'construction_loan' }
            ]);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await LoanService.getLoanTypeStatistics();
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        }
    };

    const openNew = () => {
        setLoanType({
            name: '',
            description: '',
            category: '',
            requires_collateral: false,
            max_loan_to_value: null,
            default_min_tenure_months: 6,
            default_max_tenure_months: 60,
            default_min_amount: 10000,
            default_max_amount: 1000000,
            risk_weightage: 100,
            status: 'active',
            is_default: false
        });
        setSubmitted(false);
        setNameExists(false);
        setLoanTypeDialog(true);
        setLoanTypeFields([]); // Clear fields for new loan type
        setDocumentRequirements([]); // Clear documents for new loan type
    };

    const hideDialog = () => {
        setSubmitted(false);
        setLoanTypeDialog(false);
    };

    const hideDeleteLoanTypeDialog = () => {
        setDeleteLoanTypeDialog(false);
    };

    const saveLoanType = async () => {
        setSubmitted(true);

        if (loanType.name && loanType.category) {
            try {
                // Ensure all required fields have values matching backend validation
                const loanTypeData = {
                    name: loanType.name,
                    display_name: loanType.display_name || loanType.name, // Use name as display_name if not provided
                    description: loanType.description || '',
                    category: loanType.category, // Must be one of the valid categories
                    status: loanType.status || 'active',
                    icon: loanType.icon || 'money-bill',
                    is_active: true, // Default to active
                    sort_order: loanType.sort_order || 0,
                    max_applicants: parseInt(loanType.max_applicants) || 6,
                    min_age: parseInt(loanType.min_age) || 18,
                    max_age: parseInt(loanType.max_age) || 65,
                    max_foir_percentage: parseFloat(loanType.max_foir_percentage) || 50.0,
                    requires_collateral: Boolean(loanType.requires_collateral),
                    is_default: Boolean(loanType.is_default),
                    risk_weightage: parseFloat(loanType.risk_weightage) || 100.0,
                    max_loan_to_value: loanType.requires_collateral ? (parseFloat(loanType.max_loan_to_value) || 80) : null,
                    default_min_tenure_months: parseInt(loanType.default_min_tenure_months) || 6,
                    default_max_tenure_months: parseInt(loanType.default_max_tenure_months) || 60,
                    default_min_amount: parseFloat(loanType.default_min_amount) || 10000,
                    default_max_amount: parseFloat(loanType.default_max_amount) || 1000000
                };

                let response;
                if (loanType.id) {
                    response = await LoanService.updateLoanType(loanType.id, loanTypeData);
                } else {
                    response = await LoanService.createLoanType(loanTypeData);
                }

                if (response.success) {
                    const savedLoanType = response.data?.data || response.data;
                    
                    toast.current.show({ 
                        severity: 'success', 
                        summary: 'Success', 
                        detail: `Loan type ${loanType.id ? 'updated' : 'created'} successfully`, 
                        life: 3000 
                    });
                    
                    // If it's a new loan type, update the state with the saved data including ID
                    if (!loanType.id && savedLoanType.id) {
                        setLoanType(savedLoanType);
                        // Show additional message for new loan types
                        toast.current.show({ 
                            severity: 'info', 
                            summary: 'Next Step', 
                            detail: 'You can now configure form fields and document requirements in the other tabs.', 
                            life: 5000 
                        });
                    }
                    
                    loadLoanTypes();
                    loadStatistics();
                    
                    // Don't close dialog if it's a new loan type, so user can add fields and documents
                    if (loanType.id) {
                        hideDialog();
                    } else if (savedLoanType.id) {
                        // For new loan types, also load fields and documents now that we have an ID
                        loadLoanTypeFields(savedLoanType.id);
                        loadDocumentRequirements(savedLoanType.id);
                    }
                }
            } catch (error) {
                console.error('Error saving loan type:', error);
                console.error('Error response:', error.response?.data);
                
                // Check for validation errors
                if (error.response?.data?.errors) {
                    const validationErrors = error.response.data.errors;
                    
                    // Handle name field specifically for better UX
                    if (validationErrors.name) {
                        const nameError = Array.isArray(validationErrors.name) ? validationErrors.name[0] : validationErrors.name;
                        toast.current.show({ 
                            severity: 'error', 
                            summary: 'Duplicate Name', 
                            detail: `${nameError} Please try a different name.`, 
                            life: 6000 
                        });
                    } else {
                        // Show first validation error
                        const firstError = Object.values(validationErrors)[0];
                        const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                        toast.current.show({ 
                            severity: 'error', 
                            summary: 'Validation Error', 
                            detail: errorMessage, 
                            life: 5000 
                        });
                    }
                } else {
                    const errorMessage = error.response?.data?.message || 'Failed to save loan type';
                    toast.current.show({ 
                        severity: 'error', 
                        summary: 'Error', 
                        detail: errorMessage, 
                        life: 5000 
                    });
                }
            }
        // } else if (nameExists) {
        //     toast.current.show({
        //         severity: 'error',
        //         summary: 'Cannot Save',
        //         detail: 'Please choose a different name. The current name already exists.',
        //         life: 5000
        //     });
        }
    };

    const editLoanType = (loanType) => {
        console.log('editLoanType called with:', loanType);
        setLoanType({ ...loanType });
        setNameExists(false); // Reset name validation for edit
        setLoanTypeDialog(true);
        // Load fields and documents when editing
        if (loanType.id) {
            console.log('Loading fields and documents for loan type ID:', loanType.id);
            loadLoanTypeFields(loanType.id);
            loadDocumentRequirements(loanType.id);
        } else {
            console.log('No loan type ID available for loading fields and documents');
        }
    };

    const confirmDeleteLoanType = (loanType) => {
        setLoanType(loanType);
        setDeleteLoanTypeDialog(true);
    };

    const deleteLoanType = async () => {
        try {
            const response = await LoanService.deleteLoanType(loanType.id);
            if (response.success) {
                // Reload loan types from server to ensure state is accurate
                await loadLoanTypes();
                
                setDeleteLoanTypeDialog(false);
                setLoanType({});
                
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Loan type deleted successfully', 
                    life: 3000 
                });
                
                loadStatistics();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to delete loan type';
            
            // Check if it's a conflict due to associated products
            if (error.response?.status === 409 && errorMessage.includes('associated products')) {
                const details = error.response?.data?.details;
                setAssociatedProductsCount(details?.associated_products_count || 0);
                setDeleteLoanTypeDialog(false);
                setForceDeleteDialog(true);
            } else {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: errorMessage, 
                    life: 5000 
                });
            }
        }
    };

    const forceDeleteLoanType = async () => {
        try {
            // Add force=true parameter to delete associated products too
            const response = await LoanService.deleteLoanType(loanType.id, true);
            if (response.success) {
                // Reload loan types from server to ensure state is accurate
                await loadLoanTypes();
                
                setForceDeleteDialog(false);
                setLoanType({});
                
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Loan type and associated products deleted successfully', 
                    life: 3000 
                });
                
                loadStatistics();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to delete loan type';
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: errorMessage, 
                life: 5000 
            });
        }
    };

    // Document Requirements Management
    const loadDocumentRequirements = async (loanTypeId) => {
        try {
            console.log('Loading document requirements for loan type ID:', loanTypeId);
            
            const axiosInstance = (await import('../config/axios.config')).default;
            const response = await axiosInstance.get(`/loan-types/${loanTypeId}/documents`);
            
            console.log('Document API Response:', response.data);
            
            if (response.data.success) {
                const documents = response.data.data?.documents || response.data.data || [];
                const documentsArray = Array.isArray(documents) ? documents : [];
                
                console.log('Setting document requirements:', documentsArray);
                setDocumentRequirements(documentsArray);
                
                if (documentsArray.length > 0) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Documents Loaded',
                        detail: `Loaded ${documentsArray.length} document requirements`,
                        life: 3000
                    });
                }
            } else {
                setDocumentRequirements([]);
            }
        } catch (error) {
            console.error('Error loading document requirements:', error);
            setDocumentRequirements([]);
            
            toast.current?.show({
                severity: 'error',
                summary: 'Loading Error',
                detail: 'Failed to load document requirements. Check console for details.',
                life: 4000
            });
        }
    };

    const addDocumentRequirement = async () => {
        try {
            const loanTypeId = loanType?.id;
            
            if (!loanTypeId) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please save the loan type first before adding documents'
                });
                return;
            }
            
            if (!newDocument.document_name) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Document name is required'
                });
                return;
            }

            const axiosInstance = (await import('../config/axios.config')).default;
            const response = await axiosInstance.post(`/loan-types/${loanTypeId}/documents`, {
                document_name: newDocument.document_name,
                document_type: newDocument.document_type,
                is_mandatory: Boolean(newDocument.is_mandatory),
                description: newDocument.description || '',
                alternative_documents: newDocument.alternative_documents || []
            });

            if (response.data.success) {
                setAddDocumentDialog(false);
                setNewDocument({
                    document_name: '',
                    document_type: 'identity',
                    is_mandatory: true,
                    description: '',
                    alternative_documents: []
                });
                
                // Reload documents immediately
                await loadDocumentRequirements(loanTypeId);
                
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Document requirement added successfully.'
                });
            }
        } catch (error) {
            console.error('Error adding document requirement:', error);
            
            const errorMessage = error.response?.data?.message || 'Failed to add document requirement';
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage
            });
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _loanType = { ...loanType };
        _loanType[`${name}`] = val;
        setLoanType(_loanType);
        
        // Temporarily disable frontend duplicate check to test backend validation
        // Check for duplicate names when name field changes (with small delay)
        // if (name === 'name' && val.trim()) {
        //     setTimeout(() => {
        //         checkDuplicateName(val.trim());
        //     }, 300); // Small delay to ensure state is updated
        // }
    };

    const checkDuplicateName = (name) => {
        console.log('Checking duplicate name:', name);
        console.log('Current loanTypes in state:', loanTypes.map(lt => ({ id: lt.id, name: lt.name })));
        console.log('Current loanType.id:', loanType.id);
        
        // Check if name already exists in current loan types
        const isDuplicate = loanTypes.some(lt => 
            lt.name.toLowerCase() === name.toLowerCase() && lt.id !== loanType.id
        );
        
        console.log('isDuplicate result:', isDuplicate);
        setNameExists(isDuplicate);
        
        if (isDuplicate) {
            // Generate suggestions for alternative names
            let counter = 1;
            let suggestedName = `${name} ${counter}`;
            while (loanTypes.some(lt => lt.name.toLowerCase() === suggestedName.toLowerCase())) {
                counter++;
                suggestedName = `${name} ${counter}`;
            }
            
            toast.current.show({
                severity: 'warn',
                summary: 'Name Already Exists',
                detail: `"${name}" is already used. Try: "${suggestedName}" or choose a different name.`,
                life: 6000
            });
        }
    };

    const onInputNumberChange = (e, name) => {
        const val = e.value || 0;
        let _loanType = { ...loanType };
        _loanType[`${name}`] = val;
        setLoanType(_loanType);
    };

    const onSwitchChange = (e, name) => {
        let _loanType = { ...loanType };
        _loanType[`${name}`] = e.value;
        setLoanType(_loanType);
    };

    // Field Configuration Functions (NEW)
    const configureFields = (loanType) => {
        setSelectedLoanTypeForFields(loanType);
        loadLoanTypeFields(loanType.id);
        setFieldConfigDialog(true);
    };

    const loadLoanTypeFields = async (loanTypeId) => {
        try {
            console.log('Loading fields for loan type ID:', loanTypeId);
            
            // Use existing axiosInstance from config
            const axiosInstance = (await import('../config/axios.config')).default;
            const response = await axiosInstance.get(`/loan-types/${loanTypeId}/fields`);
            
            console.log('Field API Response:', response.data);
            console.log('Field API Response structure:', JSON.stringify(response.data, null, 2));
            
            if (response.data.success) {
                const fieldsData = response.data.data?.fields || response.data.data || [];
                
                // Ensure fieldsData is an array before mapping
                const fieldsArray = Array.isArray(fieldsData) ? fieldsData : [];
                
                // Process field options (they come as JSON strings)
                const processedFields = fieldsArray.map(field => ({
                    ...field,
                    field_options: field.field_options ? 
                        (typeof field.field_options === 'string' ? 
                            JSON.parse(field.field_options) : 
                            field.field_options) : 
                        null
                }));
                
                console.log('Processed fields:', processedFields);
                console.log('Setting loanTypeFields state to:', processedFields);
                setLoanTypeFields(processedFields);
                console.log('loanTypeFields state after set:', loanTypeFields);
                
                if (processedFields.length > 0) {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Fields Loaded',
                        detail: `Loaded ${processedFields.length} configured fields`,
                        life: 3000
                    });
                }
            } else {
                setLoanTypeFields([]);
            }
        } catch (error) {
            console.error('Error loading fields:', error);
            setLoanTypeFields([]);
            
            toast.current?.show({
                severity: 'error',
                summary: 'Loading Error',
                detail: 'Failed to load field configuration. Check console for details.',
                life: 4000
            });
        }
    };

    const addFieldToLoanType = async () => {
        try {
            // Use the current loanType ID, or selectedLoanTypeForFields ID if available
            const loanTypeId = selectedLoanTypeForFields?.id || loanType?.id;
            
            if (!loanTypeId) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please save the loan type first before adding fields'
                });
                return;
            }
            
            if (!newField.field_name || !newField.field_label) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Field name and label are required'
                });
                return;
            }

            const axiosInstance = (await import('../config/axios.config')).default;
            
            // Ensure field_name follows the required pattern (lowercase, starts with letter)
            let cleanFieldName = newField.field_name.toLowerCase().replace(/[^a-z0-9_]/g, '_');
            if (cleanFieldName && !/^[a-z]/.test(cleanFieldName)) {
                cleanFieldName = 'field_' + cleanFieldName;
            }
            
            const fieldData = {
                ...newField,
                field_name: cleanFieldName,
                field_category: 'custom', // Add required field_category
                is_system_field: false,
                can_be_overridden: true,
                is_active: true,
                is_readonly: false
            };
            
            const response = await axiosInstance.post(`/loan-types/${loanTypeId}/fields`, fieldData);
            
            if (response.data.success) {
                setNewFieldDialog(false);
                setNewField({
                    field_name: '',
                    field_label: '',
                    field_type: 'text',
                    field_group: 'personal_info',
                    is_required: false,
                    applicable_to: 'all'
                });
                
                // Reload fields immediately first, then show success message
                await loadLoanTypeFields(loanTypeId);
                
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Field added successfully. The field list has been updated.'
                });
            }
        } catch (error) {
            console.error('Error adding field:', error);
            
            // Show specific validation errors if available
            const errorMessage = error.response?.data?.message || 'Failed to add field';
            const validationErrors = error.response?.data?.errors;
            
            let detailMessage = errorMessage;
            if (validationErrors && typeof validationErrors === 'object') {
                const firstError = Object.values(validationErrors)[0];
                detailMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            }
            
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: detailMessage
            });
        }
    };

    const addSampleFields = async () => {
        try {
            // Use the current loanType ID, or selectedLoanTypeForFields ID if available
            const loanTypeId = selectedLoanTypeForFields?.id || loanType?.id;
            
            if (!loanTypeId) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Please save the loan type first before adding fields'
                });
                return;
            }
            
            // Fields are already created in the database for Personal Loan
            // Just refresh the display
            await loadLoanTypeFields(loanTypeId);
            
            toast.current.show({
                severity: 'info',
                summary: 'Fields Available',
                detail: 'Sample fields are already configured for this loan type'
            });
            
        } catch (error) {
            console.error('Error loading fields:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load fields'
            });
        }
    };

    // Edit field functionality
    const editField = (field) => {
        setEditingField({ ...field });
        setEditFieldDialog(true);
    };

    const updateField = async () => {
        try {
            const loanTypeId = selectedLoanTypeForFields?.id || loanType?.id;
            
            if (!loanTypeId || !editingField?.id) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Invalid field or loan type'
                });
                return;
            }

            const axiosInstance = (await import('../config/axios.config')).default;
            const updateData = {
                field_label: editingField.field_label,
                field_description: editingField.field_description || '',
                field_type: editingField.field_type,
                field_group: editingField.field_group || 'other',
                is_required: Boolean(editingField.is_required),
                is_readonly: Boolean(editingField.is_readonly),
                can_be_overridden: Boolean(editingField.can_be_overridden),
                applicable_to: editingField.applicable_to || 'all',
                help_text: editingField.help_text || '',
                placeholder_text: editingField.placeholder_text || '',
                min_value: editingField.min_value,
                max_value: editingField.max_value,
                field_options: editingField.field_options,
                field_metadata: editingField.field_metadata || {}
            };
            
            const response = await axiosInstance.put(`/loan-types/${loanTypeId}/fields/${editingField.id}`, updateData);

            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Field updated successfully'
                });

                setEditFieldDialog(false);
                setEditingField(null);
                
                // Reload fields immediately
                await loadLoanTypeFields(loanTypeId);
            }
        } catch (error) {
            console.error('Error updating field:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to update field'
            });
        }
    };

    // Delete field functionality
    const confirmDeleteField = (field) => {
        setFieldToDelete(field);
        setDeleteFieldDialog(true);
    };

    const deleteField = async () => {
        try {
            const loanTypeId = selectedLoanTypeForFields?.id || loanType?.id;
            
            if (!loanTypeId || !fieldToDelete?.id) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Invalid field or loan type'
                });
                return;
            }

            const axiosInstance = (await import('../config/axios.config')).default;
            const response = await axiosInstance.delete(`/loan-types/${loanTypeId}/fields/${fieldToDelete.id}`);

            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Field deleted successfully'
                });

                setDeleteFieldDialog(false);
                setFieldToDelete(null);
                
                // Reload fields immediately
                await loadLoanTypeFields(loanTypeId);
            }
        } catch (error) {
            console.error('Error deleting field:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to delete field'
            });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="flex align-items-center gap-2">
                    <Button label="New" icon="pi pi-plus" className="p-button-success" onClick={openNew} />
                    <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />
                </div>
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <span className="block mt-2 md:mt-0 p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        type="search" 
                        onInput={(e) => setGlobalFilter(e.target.value)} 
                        placeholder="Search..." 
                    />
                </span>
            </React.Fragment>
        );
    };

    const categoryBodyTemplate = (rowData) => {
        const category = categories.find(cat => cat.value === rowData.category);
        return <Chip label={category?.label || rowData.category} className="text-sm" />;
    };

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.status === 'active' ? 'success' : rowData.status === 'inactive' ? 'danger' : 'warning';
        return <Tag value={rowData.status} severity={severity} />;
    };

    const collateralBodyTemplate = (rowData) => {
        return <Tag value={rowData.requires_collateral ? 'Required' : 'Not Required'} 
                   severity={rowData.requires_collateral ? 'warning' : 'success'} />;
    };

    const amountRangeBodyTemplate = (rowData) => {
        return (
            <div className="text-sm">
                <div>₹{rowData.default_min_amount?.toLocaleString()} - </div>
                <div>₹{rowData.default_max_amount?.toLocaleString()}</div>
            </div>
        );
    };

    const tenureRangeBodyTemplate = (rowData) => {
        return `${rowData.default_min_tenure_months}-${rowData.default_max_tenure_months} months`;
    };

    const riskWeightageBodyTemplate = (rowData) => {
        const severity = rowData.risk_weightage <= 50 ? 'success' : 
                        rowData.risk_weightage <= 75 ? 'warning' : 'danger';
        return <Tag value={`${rowData.risk_weightage}%`} severity={severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button 
                    icon="pi pi-cog" 
                    className="p-button-rounded p-button-info p-button-text" 
                    onClick={() => configureFields(rowData)}
                    tooltip="Configure Fields"
                />
                <Button 
                    icon="pi pi-pencil" 
                    className="p-button-rounded p-button-success p-button-text" 
                    onClick={() => editLoanType(rowData)}
                    tooltip="Edit"
                />
                <Button 
                    icon="pi pi-trash" 
                    className="p-button-rounded p-button-warning p-button-text" 
                    onClick={() => confirmDeleteLoanType(rowData)}
                    tooltip="Delete"
                />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between">
            <h5 className="m-0">Manage Loan Types</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." />
            </span>
        </div>
    );

    const loanTypeDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveLoanType} />
        </div>
    );

    const deleteLoanTypeDialogFooter = (
        <div>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteLoanTypeDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text p-button-danger" onClick={deleteLoanType} />
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    
                    {/* Statistics Cards */}
                    {statistics && (
                        <div className="grid mb-4">
                            <div className="col-12 lg:col-3">
                                <Card className="bg-blue-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Total Loan Types</span>
                                            <div className="text-900 font-medium text-xl">{statistics.total_loan_types}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-blue-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-tags text-blue-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-green-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Active Types</span>
                                            <div className="text-900 font-medium text-xl">{statistics.active_loan_types}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-green-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-check-circle text-green-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-yellow-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Secured Types</span>
                                            <div className="text-900 font-medium text-xl">{statistics.secured_vs_unsecured?.secured || 0}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-yellow-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-shield text-yellow-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                            <div className="col-12 lg:col-3">
                                <Card className="bg-purple-100">
                                    <div className="flex justify-content-between mb-3">
                                        <div>
                                            <span className="block text-500 font-medium mb-3">Categories</span>
                                            <div className="text-900 font-medium text-xl">{Object.keys(statistics.by_category || {}).length}</div>
                                        </div>
                                        <div className="flex align-items-center justify-content-center bg-purple-100 border-round" 
                                             style={{width: '2.5rem', height: '2.5rem'}}>
                                            <i className="pi pi-list text-purple-500 text-xl"></i>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                    )}

                    <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={loanTypes}
                        selection={selectedLoanTypes}
                        onSelectionChange={(e) => setSelectedLoanTypes(e.value)}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="p-datatable-gridlines"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} loan types"
                        globalFilter={globalFilter}
                        filters={filters}
                        filterDisplay="menu"
                        loading={loading}
                        responsiveLayout="scroll"
                        header={header}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                        <Column field="name" header="Name" sortable style={{ minWidth: '12rem' }} />
                        <Column field="category" header="Category" body={categoryBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                        <Column field="requires_collateral" header="Collateral" body={collateralBodyTemplate} sortable style={{ minWidth: '10rem' }} />
                        <Column header="Amount Range" body={amountRangeBodyTemplate} style={{ minWidth: '12rem' }} />
                        <Column header="Tenure Range" body={tenureRangeBodyTemplate} style={{ minWidth: '10rem' }} />
                        <Column field="risk_weightage" header="Risk %" body={riskWeightageBodyTemplate} sortable style={{ minWidth: '8rem' }} />
                        <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: '8rem' }}></Column>
                    </DataTable>

                    {/* Create/Edit Dialog with Tabs */}
                    <Dialog 
                        visible={loanTypeDialog} 
                        style={{ width: '90vw', maxWidth: '1200px' }} 
                        header={loanType.id ? `Edit Loan Type: ${loanType.name}` : 'New Loan Type'} 
                        modal 
                        className="p-fluid" 
                        footer={loanTypeDialogFooter} 
                        onHide={hideDialog}
                    >
                        <TabView>
                            {/* Basic Information Tab */}
                            <TabPanel header="Basic Information" leftIcon="pi pi-info-circle">
                                <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="name">Name *</label>
                                    <InputText 
                                        id="name" 
                                        value={loanType.name || ''} 
                                        onChange={(e) => onInputChange(e, 'name')} 
                                        required 
                                        autoFocus 
                                        className={
                                            (submitted && !loanType.name) 
                                            ? 'p-invalid' : ''
                                        }
                                    />
                                    {submitted && !loanType.name && <small className="p-invalid">Name is required.</small>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="category">Category *</label>
                                    <Dropdown 
                                        id="category"
                                        value={loanType.category} 
                                        options={categories} 
                                        onChange={(e) => onInputChange(e, 'category')} 
                                        placeholder="Select a Category"
                                        className={submitted && !loanType.category ? 'p-invalid' : ''}
                                    />
                                    {submitted && !loanType.category && <small className="p-invalid">Category is required.</small>}
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="description">Description</label>
                                    <InputTextarea 
                                        id="description" 
                                        value={loanType.description || ''} 
                                        onChange={(e) => onInputChange(e, 'description')} 
                                        rows={3} 
                                        cols={20} 
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="status">Status</label>
                                    <Dropdown 
                                        id="status"
                                        value={loanType.status} 
                                        options={statusOptions} 
                                        onChange={(e) => onInputChange(e, 'status')} 
                                        placeholder="Select Status"
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="requires_collateral">Requires Collateral</label>
                                    <InputSwitch 
                                        id="requires_collateral"
                                        checked={loanType.requires_collateral} 
                                        onChange={(e) => onSwitchChange(e, 'requires_collateral')} 
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="max_loan_to_value">Max LTV (%)</label>
                                    <InputNumber 
                                        id="max_loan_to_value" 
                                        value={loanType.max_loan_to_value} 
                                        onValueChange={(e) => onInputNumberChange(e, 'max_loan_to_value')} 
                                        mode="decimal" 
                                        minFractionDigits={2} 
                                        maxFractionDigits={2}
                                        min={0}
                                        max={100}
                                        disabled={!loanType.requires_collateral}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="default_min_amount">Min Amount (₹)</label>
                                    <InputNumber 
                                        id="default_min_amount" 
                                        value={loanType.default_min_amount} 
                                        onValueChange={(e) => onInputNumberChange(e, 'default_min_amount')} 
                                        mode="currency" 
                                        currency="INR"
                                        locale="en-IN"
                                        min={1000}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="default_max_amount">Max Amount (₹)</label>
                                    <InputNumber 
                                        id="default_max_amount" 
                                        value={loanType.default_max_amount} 
                                        onValueChange={(e) => onInputNumberChange(e, 'default_max_amount')} 
                                        mode="currency" 
                                        currency="INR"
                                        locale="en-IN"
                                        min={1000}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="default_min_tenure_months">Min Tenure (Months)</label>
                                    <InputNumber 
                                        id="default_min_tenure_months" 
                                        value={loanType.default_min_tenure_months} 
                                        onValueChange={(e) => onInputNumberChange(e, 'default_min_tenure_months')} 
                                        min={1}
                                        max={600}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="default_max_tenure_months">Max Tenure (Months)</label>
                                    <InputNumber 
                                        id="default_max_tenure_months" 
                                        value={loanType.default_max_tenure_months} 
                                        onValueChange={(e) => onInputNumberChange(e, 'default_max_tenure_months')} 
                                        min={1}
                                        max={600}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="risk_weightage">Risk Weightage (%)</label>
                                    <InputNumber 
                                        id="risk_weightage" 
                                        value={loanType.risk_weightage} 
                                        onValueChange={(e) => onInputNumberChange(e, 'risk_weightage')} 
                                        mode="decimal" 
                                        minFractionDigits={2} 
                                        maxFractionDigits={2}
                                        min={0}
                                        max={200}
                                    />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="is_default">Set as Default</label>
                                    <InputSwitch 
                                        id="is_default"
                                        checked={loanType.is_default} 
                                        onChange={(e) => onSwitchChange(e, 'is_default')} 
                                    />
                                </div>
                            </div>
                                </div>
                            </TabPanel>

                            {/* Fields Configuration Tab */}
                            <TabPanel header="Form Fields" leftIcon="pi pi-list" disabled={!loanType.id}>
                                {!loanType.id ? (
                                    <div className="text-center p-5">
                                        <i className="pi pi-info-circle text-5xl text-gray-400 mb-3"></i>
                                        <h4>Save Loan Type First</h4>
                                        <p className="text-gray-600">Please save the basic information first before configuring fields.</p>
                                    </div>
                                ) : (
                                <div className="field-configuration">
                                    <div className="flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h4 className="m-0">Dynamic Form Fields</h4>
                                            <p className="text-600 mt-1">Configure fields that will appear in loan application forms</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                label="Add Sample Fields"
                                                icon="pi pi-plus-circle"
                                                className="p-button-info"
                                                onClick={addSampleFields}
                                            />
                                            <Button
                                                label="Add Custom Field"
                                                icon="pi pi-plus"
                                                className="p-button-success"
                                                onClick={() => setNewFieldDialog(true)}
                                            />
                                        </div>
                                    </div>

                                    <DataTable
                                        value={Array.isArray(loanTypeFields) ? loanTypeFields : []}
                                        className="p-datatable-gridlines"
                                        emptyMessage="No fields configured. Save the loan type first, then configure fields."
                                        responsiveLayout="scroll"
                                    >
                                        <Column field="field_label" header="Field Label" sortable />
                                        <Column field="field_type" header="Type" body={(field) => (
                                            <Tag value={field.field_type?.toUpperCase()} />
                                        )} />
                                        <Column field="field_group" header="Group" body={(field) => (
                                            <span className="capitalize">{field.field_group?.replace(/_/g, ' ')}</span>
                                        )} />
                                        <Column field="is_required" header="Required" body={(field) => (
                                            field.is_required ? 
                                                <Tag value="Required" severity="warning" /> : 
                                                <Tag value="Optional" severity="info" />
                                        )} />
                                        <Column body={(field) => (
                                            <div className="flex gap-1">
                                                <Button
                                                    icon="pi pi-pencil"
                                                    className="p-button-rounded p-button-text p-button-sm"
                                                    tooltip="Edit Field"
                                                    onClick={() => editField(field)}
                                                />
                                                <Button
                                                    icon="pi pi-trash"
                                                    className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                    tooltip="Delete Field"
                                                    onClick={() => confirmDeleteField(field)}
                                                />
                                            </div>
                                        )} />
                                    </DataTable>
                                </div>
                                )}
                            </TabPanel>

                            {/* Documents Configuration Tab */}
                            <TabPanel header="Required Documents" leftIcon="pi pi-file" disabled={!loanType.id}>
                                {!loanType.id ? (
                                    <div className="text-center p-5">
                                        <i className="pi pi-info-circle text-5xl text-gray-400 mb-3"></i>
                                        <h4>Save Loan Type First</h4>
                                        <p className="text-gray-600">Please save the basic information first before configuring documents.</p>
                                    </div>
                                ) : (
                                <div className="document-configuration">
                                    <div className="flex justify-content-between align-items-center mb-4">
                                        <div>
                                            <h4 className="m-0">Document Requirements</h4>
                                            <p className="text-600 mt-1">Configure required documents for this loan type</p>
                                        </div>
                                        <Button
                                            label="Add Document"
                                            icon="pi pi-plus"
                                            className="p-button-success"
                                            onClick={() => setAddDocumentDialog(true)}
                                        />
                                    </div>

                                    <DataTable
                                        value={Array.isArray(documentRequirements) ? documentRequirements : []}
                                        className="p-datatable-gridlines"
                                        emptyMessage="No document requirements configured. Click 'Add Document' to add requirements."
                                        responsiveLayout="scroll"
                                    >
                                        <Column field="document_name" header="Document Name" sortable />
                                        <Column field="document_type" header="Type" body={(doc) => (
                                            <Tag value={doc.document_type?.toUpperCase()} severity="info" />
                                        )} />
                                        <Column field="is_mandatory" header="Required" body={(doc) => (
                                            doc.is_mandatory ? 
                                                <Tag value="Mandatory" severity="danger" /> : 
                                                <Tag value="Optional" severity="success" />
                                        )} />
                                        <Column body={() => (
                                            <div className="flex gap-1">
                                                <Button
                                                    icon="pi pi-pencil"
                                                    className="p-button-rounded p-button-text p-button-sm"
                                                    tooltip="Edit Document"
                                                />
                                                <Button
                                                    icon="pi pi-trash"
                                                    className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                                    tooltip="Remove Document"
                                                />
                                            </div>
                                        )} />
                                    </DataTable>
                                </div>
                                )}
                            </TabPanel>
                        </TabView>
                    </Dialog>

                    {/* Delete Dialog */}
                    <Dialog 
                        visible={deleteLoanTypeDialog} 
                        style={{ width: '450px' }} 
                        header="Confirm" 
                        modal 
                        footer={deleteLoanTypeDialogFooter} 
                        onHide={hideDeleteLoanTypeDialog}
                    >
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {loanType && <span>Are you sure you want to delete <b>{loanType.name}</b>?</span>}
                        </div>
                    </Dialog>

                    {/* Field Configuration Dialog */}
                    <Dialog
                        visible={fieldConfigDialog}
                        style={{ width: '90vw', maxWidth: '1200px', height: '80vh' }}
                        header={`Configure Fields - ${selectedLoanTypeForFields?.name || 'Loan Type'}`}
                        modal
                        maximizable
                        onHide={() => setFieldConfigDialog(false)}
                    >
                        <div className="field-configuration">
                            <div className="flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h4 className="m-0">Dynamic Form Fields</h4>
                                    <p className="text-600 mt-1">Configure fields that will appear in loan application forms</p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        label="Add Sample Fields"
                                        icon="pi pi-plus-circle"
                                        className="p-button-info"
                                        onClick={addSampleFields}
                                    />
                                    <Button
                                        label="Add Custom Field"
                                        icon="pi pi-plus"
                                        className="p-button-success"
                                        onClick={() => setNewFieldDialog(true)}
                                    />
                                </div>
                            </div>

                            <DataTable
                                value={Array.isArray(loanTypeFields) ? loanTypeFields : []}
                                className="p-datatable-gridlines"
                                emptyMessage="No fields configured for this loan type."
                                responsiveLayout="scroll"
                            >
                                <Column field="field_label" header="Field Label" sortable />
                                <Column field="field_type" header="Type" body={(field) => (
                                    <Tag value={field.field_type?.toUpperCase()} />
                                )} />
                                <Column field="field_group" header="Group" body={(field) => (
                                    <span className="capitalize">{field.field_group?.replace(/_/g, ' ')}</span>
                                )} />
                                <Column field="is_required" header="Required" body={(field) => (
                                    field.is_required || field.validation?.is_required ? 
                                        <Tag value="Required" severity="warning" /> : 
                                        <Tag value="Optional" severity="info" />
                                )} />
                                <Column field="applicable_to" header="Applies To" body={(field) => (
                                    <Tag value={field.applicable_to?.toUpperCase()} severity="help" />
                                )} />
                                <Column body={(field) => (
                                    <div className="flex gap-1">
                                        <Button
                                            icon="pi pi-pencil"
                                            className="p-button-rounded p-button-text p-button-sm"
                                            tooltip="Edit Field"
                                            onClick={() => editField(field)}
                                        />
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                            tooltip="Delete Field"
                                            onClick={() => confirmDeleteField(field)}
                                        />
                                    </div>
                                )} />
                            </DataTable>
                        </div>
                    </Dialog>

                    {/* Add New Field Dialog */}
                    <Dialog
                        visible={newFieldDialog}
                        style={{ width: '600px' }}
                        header="Add New Field"
                        modal
                        onHide={() => setNewFieldDialog(false)}
                        footer={(
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-outlined"
                                    onClick={() => setNewFieldDialog(false)}
                                />
                                <Button
                                    label="Add Field"
                                    icon="pi pi-check"
                                    onClick={addFieldToLoanType}
                                />
                            </div>
                        )}
                    >
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="field_name">Field Name *</label>
                                    <InputText
                                        id="field_name"
                                        value={newField.field_name}
                                        onChange={(e) => {
                                            let value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                                            // Ensure it starts with a letter
                                            if (value && !/^[a-z]/.test(value)) {
                                                value = 'field_' + value;
                                            }
                                            setNewField({
                                                ...newField, 
                                                field_name: value
                                            });
                                        }}
                                        placeholder="e.g., monthly_income"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="field_label">Field Label *</label>
                                    <InputText
                                        id="field_label"
                                        value={newField.field_label}
                                        onChange={(e) => setNewField({...newField, field_label: e.target.value})}
                                        placeholder="e.g., Monthly Income"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="field_type">Field Type *</label>
                                    <Dropdown
                                        id="field_type"
                                        value={newField.field_type}
                                        options={[
                                            { label: 'Text Input', value: 'text' },
                                            { label: 'Number', value: 'number' },
                                            { label: 'Currency', value: 'currency' },
                                            { label: 'Dropdown', value: 'dropdown' },
                                            { label: 'Email', value: 'email' },
                                            { label: 'Phone', value: 'phone' },
                                            { label: 'Date', value: 'date' }
                                        ]}
                                        onChange={(e) => setNewField({...newField, field_type: e.value})}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="field_group">Field Group</label>
                                    <Dropdown
                                        id="field_group"
                                        value={newField.field_group}
                                        options={[
                                            { label: 'Personal Information', value: 'personal_info' },
                                            { label: 'Employment Information', value: 'employment_info' },
                                            { label: 'Financial Information', value: 'financial_info' },
                                            { label: 'Loan Requirements', value: 'loan_requirements' },
                                            { label: 'Other', value: 'other' }
                                        ]}
                                        onChange={(e) => setNewField({...newField, field_group: e.value})}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="is_required">Required Field</label>
                                    <InputSwitch
                                        id="is_required"
                                        checked={newField.is_required}
                                        onChange={(e) => setNewField({...newField, is_required: e.value})}
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="applicable_to">Applicable To</label>
                                    <Dropdown
                                        id="applicable_to"
                                        value={newField.applicable_to}
                                        options={[
                                            { label: 'All Applicants', value: 'all' },
                                            { label: 'Primary Applicant Only', value: 'primary' },
                                            { label: 'Co-Applicants Only', value: 'co-applicant' }
                                        ]}
                                        onChange={(e) => setNewField({...newField, applicable_to: e.value})}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </Dialog>

                    {/* Edit Field Dialog */}
                    <Dialog
                        visible={editFieldDialog}
                        style={{ width: '600px' }}
                        header="Edit Field"
                        modal
                        onHide={() => setEditFieldDialog(false)}
                        footer={(
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-outlined"
                                    onClick={() => setEditFieldDialog(false)}
                                />
                                <Button
                                    label="Update Field"
                                    icon="pi pi-check"
                                    onClick={updateField}
                                />
                            </div>
                        )}
                    >
                        {editingField && (
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_field_label">Field Label *</label>
                                        <InputText
                                            id="edit_field_label"
                                            value={editingField.field_label}
                                            onChange={(e) => setEditingField({
                                                ...editingField, 
                                                field_label: e.target.value
                                            })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_field_type">Field Type *</label>
                                        <Dropdown
                                            id="edit_field_type"
                                            value={editingField.field_type}
                                            options={[
                                                { label: 'Text Input', value: 'text' },
                                                { label: 'Number', value: 'number' },
                                                { label: 'Currency', value: 'currency' },
                                                { label: 'Dropdown', value: 'dropdown' },
                                                { label: 'Email', value: 'email' },
                                                { label: 'Phone', value: 'phone' },
                                                { label: 'Date', value: 'date' },
                                                { label: 'Textarea', value: 'textarea' }
                                            ]}
                                            onChange={(e) => setEditingField({
                                                ...editingField, 
                                                field_type: e.value
                                            })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_field_group">Field Group</label>
                                        <Dropdown
                                            id="edit_field_group"
                                            value={editingField.field_group}
                                            options={[
                                                { label: 'Personal Information', value: 'personal_info' },
                                                { label: 'Employment Information', value: 'employment_info' },
                                                { label: 'Financial Information', value: 'financial_info' },
                                                { label: 'Loan Requirements', value: 'loan_requirements' },
                                                { label: 'Other', value: 'other' }
                                            ]}
                                            onChange={(e) => setEditingField({
                                                ...editingField, 
                                                field_group: e.value
                                            })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="edit_is_required">Required Field</label>
                                        <InputSwitch
                                            id="edit_is_required"
                                            checked={editingField.is_required}
                                            onChange={(e) => setEditingField({
                                                ...editingField, 
                                                is_required: e.value
                                            })}
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12">
                                    <div className="field">
                                        <label htmlFor="edit_help_text">Help Text</label>
                                        <InputTextarea
                                            id="edit_help_text"
                                            value={editingField.help_text || ''}
                                            onChange={(e) => setEditingField({
                                                ...editingField, 
                                                help_text: e.target.value
                                            })}
                                            rows={3}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog>

                    {/* Delete Field Dialog */}
                    <Dialog
                        visible={deleteFieldDialog}
                        style={{ width: '450px' }}
                        header="Confirm Delete"
                        modal
                        onHide={() => setDeleteFieldDialog(false)}
                        footer={(
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-outlined"
                                    onClick={() => setDeleteFieldDialog(false)}
                                />
                                <Button
                                    label="Delete"
                                    icon="pi pi-trash"
                                    className="p-button-danger"
                                    onClick={deleteField}
                                />
                            </div>
                        )}
                    >
                        <div className="flex align-items-center">
                            <i className="pi pi-exclamation-triangle mr-3 text-2xl text-orange-500" />
                            {fieldToDelete && (
                                <span>
                                    Are you sure you want to delete the field <strong>"{fieldToDelete.field_label}"</strong>? 
                                    This action cannot be undone.
                                </span>
                            )}
                        </div>
                    </Dialog>

                    {/* Force Delete Dialog for Loan Types with Associated Products */}
                    <Dialog
                        visible={forceDeleteDialog}
                        style={{ width: '550px' }}
                        header="Delete Loan Type with Products"
                        modal
                        onHide={() => setForceDeleteDialog(false)}
                        footer={(
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-outlined"
                                    onClick={() => setForceDeleteDialog(false)}
                                />
                                <Button
                                    label="Delete All"
                                    icon="pi pi-trash"
                                    className="p-button-danger"
                                    onClick={forceDeleteLoanType}
                                />
                            </div>
                        )}
                    >
                        <div className="flex align-items-start mb-4">
                            <i className="pi pi-exclamation-triangle mr-3 text-2xl text-orange-500" />
                            <div>
                                <h5 className="mb-2">Cannot Delete: Associated Products Found</h5>
                                {loanType && (
                                    <p className="text-600 mb-3">
                                        The loan type <strong>"{loanType.name}"</strong> has{' '}
                                        <strong>{associatedProductsCount}</strong> associated loan product(s).
                                    </p>
                                )}
                                <div className="bg-orange-50 p-3 border-round mb-3">
                                    <p className="text-sm text-600 mb-2">
                                        <strong>Warning:</strong> Proceeding will permanently delete:
                                    </p>
                                    <ul className="text-sm text-600 ml-3">
                                        <li>The loan type "{loanType?.name}"</li>
                                        <li>All {associatedProductsCount} associated loan product(s)</li>
                                        <li>Any related configuration data</li>
                                    </ul>
                                </div>
                                <p className="text-sm text-500">
                                    This action cannot be undone. Are you sure you want to proceed?
                                </p>
                            </div>
                        </div>
                    </Dialog>

                    {/* Add Document Dialog */}
                    <Dialog
                        visible={addDocumentDialog}
                        style={{ width: '600px' }}
                        header="Add Document Requirement"
                        modal
                        onHide={() => setAddDocumentDialog(false)}
                        footer={(
                            <div className="flex justify-content-end gap-2">
                                <Button
                                    label="Cancel"
                                    icon="pi pi-times"
                                    className="p-button-outlined"
                                    onClick={() => setAddDocumentDialog(false)}
                                />
                                <Button
                                    label="Add Document"
                                    icon="pi pi-check"
                                    onClick={addDocumentRequirement}
                                />
                            </div>
                        )}
                    >
                        <div className="grid">
                            <div className="col-12 md:col-8">
                                <div className="field">
                                    <label htmlFor="document_name">Document Name *</label>
                                    <InputText
                                        id="document_name"
                                        value={newDocument.document_name}
                                        onChange={(e) => setNewDocument({
                                            ...newDocument, 
                                            document_name: e.target.value
                                        })}
                                        placeholder="e.g., PAN Card, Income Certificate"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12 md:col-4">
                                <div className="field">
                                    <label htmlFor="document_type">Document Type *</label>
                                    <Dropdown
                                        id="document_type"
                                        value={newDocument.document_type}
                                        options={[
                                            { label: 'Identity', value: 'identity' },
                                            { label: 'Address', value: 'address' },
                                            { label: 'Income', value: 'income' },
                                            { label: 'Employment', value: 'employment' },
                                            { label: 'Banking', value: 'banking' },
                                            { label: 'Property', value: 'property' },
                                            { label: 'Business', value: 'business' },
                                            { label: 'Collateral', value: 'collateral' },
                                            { label: 'Other', value: 'other' }
                                        ]}
                                        onChange={(e) => setNewDocument({
                                            ...newDocument, 
                                            document_type: e.value
                                        })}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="document_description">Description</label>
                                    <InputTextarea
                                        id="document_description"
                                        value={newDocument.description}
                                        onChange={(e) => setNewDocument({
                                            ...newDocument, 
                                            description: e.target.value
                                        })}
                                        rows={3}
                                        placeholder="Additional instructions or requirements for this document"
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="is_mandatory">Mandatory Document</label>
                                    <InputSwitch
                                        id="is_mandatory"
                                        checked={newDocument.is_mandatory}
                                        onChange={(e) => setNewDocument({
                                            ...newDocument, 
                                            is_mandatory: e.value
                                        })}
                                    />
                                    <small className="block text-600 mt-1">
                                        {newDocument.is_mandatory ? 'This document is required for all applications' : 'This document is optional'}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default LoanTypes;